import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Sparkles, ArrowLeft, RefreshCw, EyeOff, Send, CheckCircle2, Pencil } from "lucide-react";
import { toast } from "sonner";
import TeaserMarkdown from "../../components/TeaserMarkdown";
import { t } from "../../lib/i18n";

export default function TeaserReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [teaser, setTeaser] = useState("");
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await api.get(`/companies/${id}`);
      setCompany(r.data);
      if (r.data.teaser_text) {
        setTeaser(r.data.teaser_text);
      } else {
        // auto-generate on first arrival
        generate(true);
      }
    })();
    // eslint-disable-next-line
  }, [id]);

  const generate = async (silent = false) => {
    setGenerating(true);
    try {
      const r = await api.post(`/companies/${id}/teaser/generate`);
      setCompany(r.data.company);
      setTeaser(r.data.teaser_text);
      if (!silent) toast.success(t("Teaser regenerado"));
    } catch (e) {
      const detail = e.response?.data?.detail || "Error generando el teaser";
      toast.error(typeof detail === "string" ? detail : "Error generando el teaser");
    } finally {
      setGenerating(false);
    }
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const r = await api.patch(`/companies/${id}/teaser`, { teaser_text: teaser });
      setCompany(r.data.company);
      toast.success(t("Teaser actualizado"));
      setEditing(false);
    } catch (e) {
      const detail = e.response?.data?.detail || "Error guardando";
      toast.error(typeof detail === "string" ? detail : "Error guardando");
    } finally {
      setSavingEdit(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const r = await api.post(`/companies/${id}/teaser/publish`);
      setCompany(r.data.company);
      toast.success(t("Teaser publicado · ya es visible para searchers"));
    } catch (e) {
      const detail = e.response?.data?.detail || "Error publicando";
      toast.error(typeof detail === "string" ? detail : "Error publicando");
    } finally {
      setPublishing(false);
    }
  };

  const unpublish = async () => {
    setPublishing(true);
    try {
      const r = await api.post(`/companies/${id}/teaser/unpublish`);
      setCompany(r.data.company);
      toast.success(t("Teaser retirado del marketplace"));
    } catch {
      toast.error(t("Error retirando"));
    } finally {
      setPublishing(false);
    }
  };

  if (!company) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <span className="font-mono-label text-muted-foreground">{t("Cargando…")}</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
        <Link
          to={`/seller/company/${id}`}
          className="font-mono-label text-muted-foreground inline-flex items-center gap-2 mb-8 hover:text-foreground"
          data-testid="teaser-back"
        >
          <ArrowLeft className="w-3 h-3" />{" "}{t("Volver al detalle de la empresa")}
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-3">
          <div>
            <span className="font-mono-label text-gold">{t("Teaser ciego ·")}{" "}{company.name}</span>
            <h1 className="font-serif-display text-4xl md:text-5xl mt-2">{t("Revisa tu teaser")}</h1>
          </div>
          {company.teaser_published ? (
            <span className="hairline px-3 py-1.5 bg-forest/10 text-forest border-forest/30 font-mono-label" data-testid="teaser-status">
              {t("Publicado · visible para searchers")}
            </span>
          ) : (
            <span className="hairline px-3 py-1.5 bg-bone font-mono-label text-muted-foreground" data-testid="teaser-status">
              {t("Borrador · aún no visible")}
            </span>
          )}
        </div>

        <p className="text-muted-foreground max-w-2xl mb-10">
          {t("Esta es la versión anónima generada por nuestra IA a partir de tus respuestas. Revísala con cuidado: es lo único que verán los searchers antes de que tú apruebes compartir más.")}
        </p>

        {/* Score badge */}
        {company.score != null && (
          <div className="hairline inline-flex items-center gap-3 px-4 py-3 bg-bone mb-8">
            <Sparkles className="w-4 h-4 text-gold" />
            <span className="font-mono-label text-gold">{t("Score IA")}</span>
            <span className="font-serif-display text-2xl text-gold">{company.score}<span className="text-xs text-muted-foreground">/100</span></span>
            <span className="text-sm text-muted-foreground max-w-md">{company.score_rationale}</span>
          </div>
        )}

        {/* TEASER CARD */}
        <div className="hairline bg-card">
          <div className="px-7 py-4 hairline-b bg-[#1E3A5F] text-bone flex items-center gap-3">
            <EyeOff className="w-4 h-4" />
            <span className="font-mono-label text-gold">{t("Versión que verán los searchers")}</span>
          </div>
          <div className="p-8 lg:p-10">
            {editing ? (
              <Textarea
                value={teaser}
                onChange={(e) => setTeaser(e.target.value)}
                rows={20}
                className="rounded-none font-mono text-sm w-full"
                data-testid="teaser-editor"
              />
            ) : (
              <article className="teaser-prose" data-testid="teaser-content">
                <TeaserMarkdown text={teaser || t("Generando teaser…")} />
              </article>
            )}
          </div>

          <div className="px-7 py-5 hairline-t bg-bone flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-2">
              {editing ? (
                <>
                  <Button
                    onClick={saveEdit}
                    disabled={savingEdit}
                    className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-10 px-5"
                    data-testid="teaser-save-edit"
                  >
                    {savingEdit ? t("Guardando…") : t("Guardar cambios")}
                  </Button>
                  <Button
                    onClick={() => { setEditing(false); setTeaser(company.teaser_text || ""); }}
                    variant="outline"
                    className="rounded-none h-10 px-5"
                  >
                    {t("Cancelar")}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => setEditing(true)}
                    variant="outline"
                    disabled={!teaser}
                    className="rounded-none h-10 px-5"
                    data-testid="teaser-edit-btn"
                  >
                    <Pencil className="w-3 h-3 mr-2" />{" "}{t("Editar")}
                  </Button>
                  <Button
                    onClick={() => generate(false)}
                    variant="outline"
                    disabled={generating}
                    className="rounded-none h-10 px-5"
                    data-testid="teaser-regenerate-btn"
                  >
                    <RefreshCw className={`w-3 h-3 mr-2 ${generating ? "animate-spin" : ""}`} /> {generating ? t("Regenerando…") : t("Regenerar con IA")}
                  </Button>
                </>
              )}
            </div>

            {!editing && (
              company.teaser_published ? (
                <Button
                  onClick={unpublish}
                  disabled={publishing}
                  variant="outline"
                  className="rounded-none h-10 px-5 border-destructive text-destructive hover:bg-destructive/5"
                  data-testid="teaser-unpublish-btn"
                >
                  {t("Retirar del marketplace")}
                </Button>
              ) : (
                <Button
                  onClick={publish}
                  disabled={publishing || !teaser}
                  className="rounded-none bg-forest hover:bg-forest/90 text-bone h-10 px-6"
                  style={{ backgroundColor: "#2E6F80" }}
                  data-testid="teaser-publish-btn"
                >
                  <Send className="w-3 h-3 mr-2" /> {publishing ? t("Publicando…") : t("Publicar para searchers")}
                </Button>
              )
            )}
          </div>
        </div>

        {company.teaser_published && (
          <div className="mt-6 hairline p-5 bg-forest/5 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-forest mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <div className="font-medium">{t("Tu teaser está publicado.")}</div>
              <div className="text-muted-foreground">
                {t("Aparece en el marketplace de searchers como")}{" "}
                <span className="font-mono-label">{t("\"Proyecto")}{" "}{(company.name || "X").charAt(0).toUpperCase()} · {company.sector}"</span>{t(". Cuando alguno exprese interés, te avisaremos en tu panel.")}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
